import { ReferralOutput } from '@/types/pos/referral'

export const REFERRAL_DASHBOARD_TOKEN_KEY = 'vcashReferralDashboardToken'
const REFERRAL_LAST_SHOWN_PREFIX = 'vcashReferralLastShown:'
const REMINDER_INTERVAL_MS = 10 * 24 * 60 * 60 * 1000

export const storeReferralDashboardToken = (token: string) => {
    if (typeof window === 'undefined') return
    const trimmed = token.trim()
    if (trimmed.length !== 22) return
    window.localStorage.setItem(REFERRAL_DASHBOARD_TOKEN_KEY, trimmed)
}

export const getReferralDashboardToken = (): string | null => {
    if (typeof window === 'undefined') return null
    const token = window.localStorage
        .getItem(REFERRAL_DASHBOARD_TOKEN_KEY)
        ?.trim()
    if (!token || token.length !== 22) return null
    return token
}

export const clearReferralDashboardToken = () => {
    if (typeof window === 'undefined') return
    window.localStorage.removeItem(REFERRAL_DASHBOARD_TOKEN_KEY)
}

const getLastShownKey = (referralAccountId: string) =>
    `${REFERRAL_LAST_SHOWN_PREFIX}${referralAccountId}`

export const markReferralScreenShown = (referralAccountId: string) => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(
        getLastShownKey(referralAccountId),
        new Date().toISOString()
    )
}

export const isContextTokenActive = (referral: ReferralOutput) => {
    if (!referral.contextToken || !referral.contextTokenExpiresAt) return false
    const expiry = Date.parse(referral.contextTokenExpiresAt)
    if (Number.isNaN(expiry)) return false
    return Date.now() < expiry
}

/**
 * Phone-enrollment prompt: only for players who are not yet enrolled and still
 * have a usable context token. Never construct or expect a referral code here.
 *
 * Enrolled players are directed to the external dashboard (not this terminal UI).
 */
export const shouldShowReferralEnrollmentScreen = (referral: ReferralOutput) => {
    if (referral.isEnrolled) return false
    return isContextTokenActive(referral)
}

/**
 * Reminder / promo signal for enrolled players (stats only). This terminal does
 * not render a dashboard; callers may use this for future soft prompts.
 */
export const shouldRemindEnrolledPlayer = (referral: ReferralOutput) => {
    if (!referral.isEnrolled) return false
    if (typeof window === 'undefined') return false

    const lastShown = window.localStorage.getItem(
        getLastShownKey(referral.referralAccountId)
    )
    if (!lastShown) return true

    const lastShownAt = Date.parse(lastShown)
    if (Number.isNaN(lastShownAt)) return true

    return Date.now() - lastShownAt >= REMINDER_INTERVAL_MS
}

/** @deprecated Use shouldShowReferralEnrollmentScreen */
export const shouldShowReferralScreen = shouldShowReferralEnrollmentScreen

export const normalizePromoCode = (value: string) =>
    value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase()
